import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationPrompt: React.FC = () => {
  const { isSupported, permission, isSubscribed, subscribe, isLoading, error } = useNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  // Check if banner was previously dismissed
  useEffect(() => {
    const wasDismissed = localStorage.getItem('notification-prompt-dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  // Check if backend returned unavailable error
  useEffect(() => {
    if (error && (error.includes('not available') || error.includes('server'))) {
      setBackendUnavailable(true);
    }
  }, [error]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('notification-prompt-dismissed', 'true');
  };

  const handleEnable = async () => {
    try {
      await subscribe();
      setDismissed(true);
    } catch (error: any) {
      // Error is handled by the context
      console.error('Failed to subscribe:', error);
      // If backend is unavailable, hide prompt
      if (error.message && (error.message.includes('not available') || error.message.includes('server'))) {
        setBackendUnavailable(true);
        handleDismiss(); // Auto-dismiss if backend unavailable
      }
    }
  };

  // Don't show if:
  // - Not supported
  // - Already subscribed
  // - Permission denied
  // - Dismissed
  // - Backend unavailable
  if (!isSupported || isSubscribed || permission === 'denied' || dismissed || backendUnavailable) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
            <Bell className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-1">
              Ative as Notificações Push 🔔
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              Receba notificações em tempo real quando uma nova reserva for confirmada. 
              Nunca perca uma reserva importante!
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleEnable}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded transition-colors disabled:opacity-50 flex items-center space-x-1"
              >
                <Bell className="h-4 w-4" />
                <span>{isLoading ? 'Ativando...' : 'Ativar Agora'}</span>
              </button>
              <button
                onClick={handleDismiss}
                className="bg-white hover:bg-blue-50 text-blue-700 text-sm font-medium py-2 px-4 rounded border border-blue-200 transition-colors"
              >
                Mais Tarde
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0 ml-2"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default NotificationPrompt;

